export const ENERGY_TOKEN_REGEX = /\benergy\b/gi;
export const VALID_FORMULA_CHARACTERS_REGEX = /^[\d.+\-*/%() \t]*$/;
const CONSECUTIVE_OPERATORS_REGEX = /[+\-*/][+\-*/]/;

const getNonWhitespaceLeftIndex = (
  expression: string,
  startIndex: number
): number => {
  let index = startIndex;
  while (index >= 0 && /\s/.test(expression[index])) {
    index--;
  }

  return index;
};

const getNonWhitespaceRightIndex = (
  expression: string,
  startIndex: number
): number => {
  let index = startIndex;
  while (index < expression.length && /\s/.test(expression[index])) {
    index++;
  }

  return index;
};

const validatePercentagePosition = (
  expression: string,
  percentageIndex: number
): string | undefined => {
  const leftIndex = getNonWhitespaceLeftIndex(expression, percentageIndex - 1);
  const rightIndex = getNonWhitespaceRightIndex(
    expression,
    percentageIndex + 1
  );

  const leftChar = leftIndex >= 0 ? expression[leftIndex] : '';
  const rightChar =
    rightIndex < expression.length ? expression[rightIndex] : '';

  if (!/[\d)]/.test(leftChar)) {
    return 'Percentage (%) must follow a number, energy, or closing parenthesis';
  }

  if (rightChar && !/[+\-*/)]/.test(rightChar)) {
    return 'Invalid percentage usage. Use % only as a percentage suffix, e.g. 50%';
  }

  return undefined;
};

export const normalisePercentageExpression = (
  expression: string
): { expression: string; error?: string } => {
  let searchFrom = 0;
  let percentageIndex = expression.indexOf('%', searchFrom);
  while (percentageIndex !== -1) {
    const percentageError = validatePercentagePosition(
      expression,
      percentageIndex
    );
    if (percentageError) {
      return { expression, error: percentageError };
    }

    searchFrom = percentageIndex + 1;
    percentageIndex = expression.indexOf('%', searchFrom);
  }

  // Convert postfix percentage to division in a single linear pass.
  // Example: "50%" -> "50/100", "(1+2)%" -> "(1+2)/100"
  let normalised = '';
  for (let idx = 0; idx < expression.length; idx++) {
    normalised += expression[idx] === '%' ? '/100' : expression[idx];
  }

  return { expression: normalised };
};

const validateParentheses = (expression: string): true | string => {
  let depth = 0;
  for (const ch of expression) {
    if (ch === '(') {
      depth++;
    } else if (ch === ')') {
      depth--;
    }
    if (depth < 0) {
      return 'Formula has unmatched closing parenthesis';
    }
  }

  return depth === 0 ? true : 'Formula has unmatched opening parenthesis';
};

const validateOperatorUsage = (expression: string): true | string => {
  const withoutLeadingSpaces = expression.trimStart();
  const withoutTrailingSpaces = expression.trimEnd();

  // Disallow unary + / - directly before energy. Only binary usage like
  // "100 - energy" should be allowed.
  if (/(^|[+(\-*/])\s*[+\-]\s*energy\b/i.test(expression)) {
    return 'Formula cannot use unary + or - directly before energy';
  }

  if (CONSECUTIVE_OPERATORS_REGEX.test(expression)) {
    return 'Formula contains consecutive operators which is not allowed';
  }

  if (/^[+\-*/]/.test(withoutLeadingSpaces)) {
    return 'Formula cannot start with +, -, * or /';
  }

  if (/[+\-*/]$/.test(withoutTrailingSpaces)) {
    return 'Formula cannot end with an operator except for %';
  }

  if (/[+\-*/]\s*\)/.test(expression)) {
    return 'Formula has an operator immediately before a closing parenthesis';
  }

  if (/\(\s*[*/]/.test(expression)) {
    return 'Formula has an invalid operator after an opening parenthesis';
  }

  return /\(\s*\)/.test(expression)
    ? 'Formula contains empty parentheses'
    : true;
};

const validateNumericTokens = (expression: string): true | string => {
  const tokenCheck = expression.replace(/[+\-*/()]/g, ' ');
  const parts = tokenCheck.split(/\s+/).filter(Boolean);
  for (const part of parts) {
    if (Number.isNaN(Number(part))) {
      return `"${part}" is not a valid number in the formula`;
    }
  }

  return true;
};

export const validateFormulaExpression = (
  expression: string
): true | string => {
  const parenthesesValidation = validateParentheses(expression);
  if (parenthesesValidation !== true) {
    return parenthesesValidation;
  }

  const operatorsValidation = validateOperatorUsage(expression);
  if (operatorsValidation !== true) {
    return operatorsValidation;
  }

  return validateNumericTokens(expression);
};
